import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { PromoDesignSystemConfiguration } from '@frontend/promo-utils';
import { ClientConfigService, EventsService, NativeEventType, SimpleEvent, UserService, VanillaEventNames } from '@frontend/vanilla/core';
import { LoyaltyLoginService } from '@frontend/vanilla/features/loyalty-login';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subject, filter, takeUntil } from 'rxjs';

import { Offer } from '../../offers/models';
import { OfferContentConfiguration } from '../../offers/offer-content.client-config';
import { LoadOffersApiService } from '../../offers/offers-service/load-offers.service';
import { OffersSkeletonComponent } from '../../shared-offers/offers-skeleton/offers-skeleton.component';
import { BetStationConfiguration } from '../bet-station-client-config';
import { BetstationOfferService } from '../bet-station-offer.service';
import { BetStationOfferTileTeaserComponent } from '../bet-station-offers-teaser/bet-station-offer-tile-teaser.component';
import { BetstationOverlayDialogModal } from '../bet-station-overlay-dialog-modal';

@Component({
    selector: 'ph-bet-station-offer-tiles',
    templateUrl: 'bet-station-offer-tiles.component.html',
    imports: [NgScrollbarModule, OffersSkeletonComponent, BetStationOfferTileTeaserComponent],
})
export class BetStationOfferTilesComponent implements OnInit, OnDestroy {
    @Input() shouldLoadPromoClientConfig: boolean = true;
    offersAreLoading: boolean;
    offerDetailsList: Offer[];
    promoClientConfigLoaded: boolean;

    private readonly destroy$ = new Subject<void>();
    routeSub: any;

    constructor(
        private betStationConfiguration: BetStationConfiguration,
        private userService: UserService,
        private clientConfigService: ClientConfigService,
        public loadOffersApiService: LoadOffersApiService,
        private designSystemConfig: PromoDesignSystemConfiguration,
        private offerContentConfig: OfferContentConfiguration,
        private activatedRoute: ActivatedRoute,
        private betstationOfferService: BetstationOfferService,
        private loyaltyLoginService: LoyaltyLoginService,
        private eventService: EventsService,
        private overlayDialogModal: BetstationOverlayDialogModal,
    ) {}

    async ngOnInit() {
        await this.loadPromoClientConfig();
        this.offersAreLoading = true;
        this.loadOffersApiService.loadBetStationSpecialOffers();
        this.loadOffersApiService.loadClientContent();

        this.loadOffersAfterLogin();
        this.routeSub = this.activatedRoute.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const isFreeToPlay = params.get('oneTwoFree') ?? 'false';
            const slipId = params.get('slipId') ?? '';
            this.openOneTwoFreeOffer(isFreeToPlay, slipId);
        });

        this.loadOffersApiService.loadBetStationSpecialOffers$.pipe(takeUntil(this.destroy$)).subscribe((offerDetailsList: Offer[]) => {
            this.offerDetailsList = offerDetailsList;
            const isFreeToPlay = this.activatedRoute.snapshot.queryParamMap.get('oneTwoFree') ?? 'false';
            const slipid = this.activatedRoute.snapshot.queryParamMap.get('slipId') ?? '';
            this.openOneTwoFreeOffer(isFreeToPlay, slipid);
            this.offersAreLoading = false;
        });
    }

    openOneTwoFreeOffer(isFreeToPlay: string, slipId: string) {
        if (isFreeToPlay == 'true' && this.offerDetailsList?.length > 0) {
            const offer = this.offerDetailsList.find((x) => x.offerMetadata.specialOfferName == 'onetwofree') ?? ({} as Offer);
            const offerIndex = this.offerDetailsList.findIndex((x) => x.offerMetadata.specialOfferName == 'onetwofree');
            const isPopUpOpened = this.betstationOfferService.getPopupInfo();
            if (Object.keys(offer)?.length > 0 && !isPopUpOpened) {
                this.betstationOfferService.updatePopupInfo(true);
                const requiredModalData = this.betstationOfferService.navigateToOfferMoreInfo(offerIndex, offer, slipId);
                this.overlayDialogModal.openDialogModal(requiredModalData);
            }
        }
    }

    async loadPromoClientConfig(): Promise<void> {
        if (this.shouldLoadPromoClientConfig) {
            const configs = [] as any;
            if (!this.betStationConfiguration.isConfigReady) configs.push(BetStationConfiguration);
            if (!this.designSystemConfig.isConfigReady) configs.push(PromoDesignSystemConfiguration);
            if (!this.offerContentConfig.isConfigReady) configs.push(OfferContentConfiguration);

            if (configs.length > 0) await this.clientConfigService.load('', configs);
        }
        this.promoClientConfigLoaded = true;
    }

    private loadOffersAfterLogin() {
        if (
            this.loyaltyLoginService.loyaltyValidation != null &&
            Object.keys(this.loyaltyLoginService.loyaltyValidation)?.length > 0 &&
            this.userService.isAuthenticated
        ) {
            this.loadOffersApiService.loadBetStationSpecialOffers();
        }

        this.loyaltyLoginService.loyaltyLoginValidation.subscribe((res: any) => {
            if (res != null && Object.keys(res)?.length > 0 && this.userService.isAuthenticated) {
                this.loadOffersApiService.loadBetStationSpecialOffers();
            }
        });

        this.eventService.events
            .pipe(
                filter((e: SimpleEvent) => e?.eventName === VanillaEventNames.EVENT_SENT_TO_NATIVE && NativeEventType.LOGOUT === e?.data?.eventName),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.loadOffersApiService.loadBetStationSpecialOffers();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
